import { CallerIdentity } from "@/types/RealtimeProtocol";

/**
 * Normalizes a string identifier or existing CallerIdentity into a standardized CallerIdentity.
 */
export function toCallerIdentity(
  caller: CallerIdentity | string,
): CallerIdentity {
  if (typeof caller === "string") {
    const isEmail = caller.includes("@");
    const normalized = caller.trim().toLowerCase();
    return {
      uuid: normalized,
      session_id: `session_${normalized}`,
      name: caller.trim(),
      username: isEmail ? null : caller.trim(),
      email: isEmail ? normalized : null,
      call_as: isEmail ? "email" : "name",
    };
  }

  const uuid = (caller.uuid || "").trim().toLowerCase();
  return {
    uuid,
    session_id: caller.session_id || `session_${uuid}`,
    name: caller.name || caller.uuid || "",
    username: caller.username ? caller.username.trim().toLowerCase() : null,
    email: caller.email ? caller.email.trim().toLowerCase() : null,
    call_as: caller.call_as || (caller.email ? "email" : "name"),
  };
}

/**
 * Determines whether candidate matches target caller identity.
 * Compares by UUID, username, name, email, and session_id (case-insensitive).
 * Also performs cross-identifier comparisons between uuid, username, and name.
 */
export function matchesCallerIdentity(
  candidate: CallerIdentity | string | null | undefined,
  target: CallerIdentity | string | null | undefined,
): boolean {
  if (!candidate || !target) return false;

  const cand = toCallerIdentity(candidate);
  const tgt = toCallerIdentity(target);

  // Exact session match
  if (cand.session_id && tgt.session_id && cand.session_id === tgt.session_id) {
    return true;
  }

  // Email match
  if (cand.email && tgt.email && cand.email === tgt.email) {
    return true;
  }

  // Direct attribute matches
  if (cand.uuid && tgt.uuid && cand.uuid === tgt.uuid) {
    return true;
  }
  if (cand.username && tgt.username && cand.username === tgt.username) {
    return true;
  }
  if (
    cand.name &&
    tgt.name &&
    cand.name.trim().toLowerCase() === tgt.name.trim().toLowerCase()
  ) {
    return true;
  }

  // Cross-identifier matches (e.g. username matches uuid or name, uuid matches name)
  const candIdentifiers = [
    cand.uuid,
    cand.username,
    cand.name?.trim().toLowerCase(),
  ].filter(Boolean) as string[];

  const tgtIdentifiers = [
    tgt.uuid,
    tgt.username,
    tgt.name?.trim().toLowerCase(),
  ].filter(Boolean) as string[];

  for (const cId of candIdentifiers) {
    if (tgtIdentifiers.includes(cId)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks whether candidate caller identity matches any identifier in a set.
 */
export function matchesAnyIdentifier(
  candidate: CallerIdentity | string | null | undefined,
  identifiers: Set<string>,
  sessionId?: string,
): boolean {
  if (!candidate || !identifiers || identifiers.size === 0) return false;

  if (typeof candidate === "string") {
    return identifiers.has(candidate.trim().toLowerCase());
  }

  if (sessionId && candidate.session_id && candidate.session_id === sessionId) {
    return true;
  }

  if (candidate.uuid && identifiers.has(candidate.uuid.trim().toLowerCase())) {
    return true;
  }
  if (
    candidate.username &&
    identifiers.has(candidate.username.trim().toLowerCase())
  ) {
    return true;
  }
  if (candidate.name && identifiers.has(candidate.name.trim().toLowerCase())) {
    return true;
  }
  if (
    candidate.email &&
    identifiers.has(candidate.email.trim().toLowerCase())
  ) {
    return true;
  }

  return false;
}

export interface BuildSenderIdentityOptions {
  user?: {
    uid: string;
    displayName?: string | null;
    email?: string | null;
    isAnonymous?: boolean;
  } | null;
  userInDb?: {
    data: {
      username?: string;
      name?: string;
    };
  } | null;
  callerId: string;
  sessionId?: string;
}

/**
 * Builds a standardized CallerIdentity for outbound messages, knocks, and sync requests.
 */
export function buildSenderIdentity({
  user,
  userInDb,
  callerId,
  sessionId = "controller-preview-session",
}: BuildSenderIdentityOptions): CallerIdentity {
  const uuid = (user?.uid || callerId).trim().toLowerCase();
  const username =
    userInDb?.data.username?.trim().toLowerCase() ||
    (!user || user.isAnonymous ? callerId.trim().toLowerCase() : null);
  const email = user?.email?.trim().toLowerCase() || null;
  const name = user?.displayName || userInDb?.data.name || callerId;
  const call_as = userInDb?.data.username
    ? "username"
    : user?.email
      ? "email"
      : "name";

  return {
    uuid,
    session_id: sessionId,
    name,
    username,
    email,
    call_as,
  };
}
